import { NgModule } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { LAZYLOAD_IMAGE_HOOKS, LazyLoadImageModule } from 'ng-lazyload-image';
import { MatDivider } from '@angular/material/divider';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { FormsModule } from '@angular/forms';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatStepperModule } from '@angular/material/stepper';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { NgxsFormDirective } from '@ngxs/form-plugin';
import { MatTabsModule } from '@angular/material/tabs';
import { NgSelectModule } from '@ng-select/ng-select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatRadioModule } from '@angular/material/radio';
import { NgxMaskDirective } from 'ngx-mask';
import { NgxPermissionsModule } from 'ngx-permissions';
import { NgxMaterialIntlTelInputComponent } from 'ngx-material-intl-tel-input';
import { AngularSvgIconModule } from 'angular-svg-icon';

import { CustomLazyLoadImageHooks } from '../utils/lazy-load';
import { ToolbarComponent } from '../components/toolbar/toolbar.component';
import { AdminFooterComponent } from '../components/admin-footer/admin-footer.component';
import { SearchBarComponent } from '../components/search/search-bar/search-bar.component';
import { SearchBarInputComponent } from '../components/search/search-bar-input/search-bar-input.component';
import { CustomModalCardComponent } from '../components/modals/custom-modal-card/custom-modal-card.component';
import { DefaultModalHeaderComponent } from '../components/modals/default-modal-header/default-modal-header.component';
import { DeleteConfirmComponent } from '../components/modals/delete-confirm/delete-confirm.component';
import { PageHeaderComponent } from '../components/page-header/page-header.component';
import { PageNamePipe } from '../pipes/page-name.pipe';
import { LoadingOrErrorComponent } from '../components/loading-or-error/loading-or-error.component';
import { EmptyCardComponent } from '../components/empty-card/empty-card.component';
import { UseFunctionPipe } from '../pipes/use-function.pipe';
import { PageHeaderChildComponent } from '../components/page-header-child/page-header-child.component';
import { DateTimeFormatPipe } from '../pipes/date-time-format.pipe';
import { UiModule } from '../components/ui/ui.module';
import { RequiredAsteriskDirective } from '../directives/required-asterisk.directive';
import { ReferenceDataTypePipe } from '../pipes/reference-data-type.pipe';
import { UserInfosComponent } from '../components/user-infos/user-infos.component';
import { TruncateTextPipe } from '../pipes/truncate-text.pipe';
import { StickerMovementTypePipe } from '../pipes/sticker-movement-type.pipe';
import { TransactionStatusPipe } from '../pipes/transaction-status.pipe';
import { PaymentMethodPipe } from '../pipes/payment-method.pipe';
import { PhoneNumberPipe } from '../pipes/phone-number.pipe';
import { PageComponent } from '../components/page/page.component';
import { PaymentTriggerTypePipe } from '../pipes/payment-trigger-type.pipe';
import { CountryPipe } from '../pipes/country.pipe';
import { CustomerSupplierTypePipe } from '../pipes/customer-supplier-type.pipe';
import { InvoiceFormComponent } from '../components/modals/invoice-form/invoice-form.component';
import { InvoiceTypeLabelPipe } from '../pipes/invoice-type-label.pipe';
import { FormErrorComponent } from '../components/form-error/form-error.component';
import { CurrencyPipe } from '../pipes/currency.pipe';
import { BillingTypeLabelPipe } from '../pipes/billing-type-label.pipe';
import { InvoicePaymentMethodPipe } from '../pipes/invoice-payment-method.pipe';
import { BillingAddressComponent } from '../components/billing-address/billing-address.component';
import { InvoiceStatusPipe } from '../pipes/invoice-status.pipe';
import { InvoiceStatusTagComponent } from '~backoffice/app/invoices/components/invoice-status-tag/invoice-status-tag.component';
import { DisableAutocompleteDirective } from '../directives/disable-autocomplete.directive';
import { TextEllipsisComponent } from '../components/text-ellipsis/text-ellipsis.component';
import { IntegerOnlyDirective } from '../directives/no-decimal.directive';
import { CompanyVerificationSuccessComponent } from '~backoffice/app/public-verification/components/company-verification-success/company-verification-success.component';
import { TooltipDirective } from '../directives/app-tooltip.directive';
import { PhoneNumberPrefixDirective } from '../directives/phone-number-prefix.directive';
import { BillingEquipmentPipe } from '../pipes/biiling-equipment.pipe';
import { OverlayBlockerComponent } from '../components/overlay-blocker/overlay-blocker.component';
import { OnboardingEquipmentPipe } from '../pipes/onboarding-equipment.pipe';
import { RoundingRuleLabelPipe } from '../pipes/rounding-rule-label.pipe';
import { ElementBoundingDirective } from '~core/directives/element-bounding.directive';
import { TruncatePipe } from '../pipes/truncate.pipe';
import { SmartTitleCasePipe } from '~core/pipes/smart-title-case.pipe';
import { AmountInWordsPipe } from '~core/pipes/amount-in-words.pipe';
import { AppCopyrightComponent } from '../components/app-copyright/app-copyright.component';
import { PhoneFieldComponent } from '~ui-kit/inputs/phone-field';
import { PriceFormatPipe } from '~core/pipes/price-format.pipe';
import { UnitMeasureTypePipe } from '~core/pipes/unit-measure-type.pipe';

const DECLARATIONS = [
  ToolbarComponent,
  SearchBarComponent,
  SearchBarInputComponent,
  AdminFooterComponent,
  CustomModalCardComponent,
  DefaultModalHeaderComponent,
  DeleteConfirmComponent,
  PageHeaderComponent,
  PageHeaderChildComponent,
  EmptyCardComponent,
  PageHeaderChildComponent,
  UserInfosComponent,
  PageComponent,
  InvoiceFormComponent,
  FormErrorComponent,
  BillingAddressComponent,
  InvoiceStatusTagComponent,
  TextEllipsisComponent,
  CompanyVerificationSuccessComponent,
  OverlayBlockerComponent
];

const DIRECTIVES = [
  NgxsFormDirective,
  RequiredAsteriskDirective,
  NgxMaskDirective,
  DisableAutocompleteDirective,
  IntegerOnlyDirective,
  TooltipDirective,
  PhoneNumberPrefixDirective
];

const MODULES = [
  CommonModule,
  ReactiveFormsModule,
  RouterModule,
  NgOptimizedImage,
  MatIconModule,
  LazyLoadImageModule,
  MatMenuModule,
  MatPaginatorModule,
  MatSortModule,
  MatTableModule,
  MatTooltipModule,
  MatButtonModule,
  MatIconModule,
  MatFormFieldModule,
  MatSelectModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatDialogModule,
  MatInputModule,
  FormsModule,
  NgOptimizedImage,
  MatSlideToggleModule,
  MatStepperModule,
  MatAutocompleteModule,
  PageNamePipe,
  UseFunctionPipe,
  DateTimeFormatPipe,
  ReferenceDataTypePipe,
  TruncateTextPipe,
  MatTabsModule,
  MatDivider,
  UiModule,
  AngularSvgIconModule,
  NgSelectModule,
  MatSidenavModule,
  MatRadioModule,
  StickerMovementTypePipe,
  TransactionStatusPipe,
  PaymentMethodPipe,
  PhoneNumberPipe,
  PaymentTriggerTypePipe,
  CountryPipe,
  CustomerSupplierTypePipe,
  InvoiceTypeLabelPipe,
  CurrencyPipe,
  BillingTypeLabelPipe,
  InvoicePaymentMethodPipe,
  InvoiceStatusPipe,
  InvoiceTypeLabelPipe,
  BillingEquipmentPipe,
  OnboardingEquipmentPipe,
  RoundingRuleLabelPipe,
  LoadingOrErrorComponent,
  NgxPermissionsModule,
  ElementBoundingDirective,
  TruncatePipe,
  SmartTitleCasePipe,
  AmountInWordsPipe,
  AppCopyrightComponent,
  NgxMaterialIntlTelInputComponent,
  PhoneFieldComponent,
  PriceFormatPipe,
  UnitMeasureTypePipe
];

const PROVIDERS = [
  {
    provide: LAZYLOAD_IMAGE_HOOKS,
    useClass: CustomLazyLoadImageHooks
  }
];

@NgModule({
  declarations: DECLARATIONS,
  imports: [...MODULES, ...DIRECTIVES],
  exports: [...MODULES, ...DECLARATIONS, ...DIRECTIVES],
  providers: PROVIDERS
})
export class SharedModule {}
